import { axios } from "@/plugins/axios";
import { KeyValue, Resource } from "@/helpers/Interfaces";
import { IMetaModel } from "@/modules/core/common/services/GridService";
import { FieldIndex } from '@/modules/low-code/unique-keys/services/UniqueKeysService';

export default class HomeService
{
    private dashboardApiUrl: string;
    private configApiUrl: string;

    public constructor(dashboardApiUrl: string = 'widgets-user-dashboard', configApiUrl: string = 'widgets-user-config')
    {
        this.dashboardApiUrl = dashboardApiUrl;
        this.configApiUrl = configApiUrl;
    }

    public async getAvailableWidgets(filter?: FormFilter): Promise<Array<APIWidget>>
    {
        return (
            await axios.get(`${this.dashboardApiUrl}/dictionary/widgets`, {
                params: filter,
            })
        ).data;
    }

    public async getSettings(sitemapId?: string): Promise<WidgetSettings>
    {
        return (await axios.get(`${this.dashboardApiUrl}/config/${sitemapId || ""}`)).data;
    }

    public async saveSettings(publicId: string, form: any): Promise<any>
    {
        return (await axios.put(`simple-command/${this.dashboardApiUrl}/${publicId}`, form)).data;
    }

    public async getWidgetUserConfig(publicId: string): Promise<Resource<WidgetModel>>
    {
        return (await axios.get(`${this.configApiUrl}/${publicId}`)).data;
    }

    public async updateWidgetUserConfig(publicId: string, form: any, urlParams: any = {}): Promise<any>
    {
        return (await axios.put(`simple-command/${this.configApiUrl}/${publicId}`, form, {
            params: urlParams
        })).data;
    }

    public async getWidget(publicId: string): Promise<APIWidget>
    {
        return (await axios.get(`${this.configApiUrl}/${publicId}`)).data.result;
    }

    public async getWidgets(publicIds: string[]): Promise<Resource<APIWidget>[]>
    {
        return (await axios.get(this.configApiUrl, {
            params: { publicIds: publicIds.join(',') }
        })).data;
    }

    public async initSettings(form: any): Promise<any>
    {
        return (await axios.post(`simple-command/${this.dashboardApiUrl}`, form)).data;
    }

    public async resetSettings(publicId: string): Promise<any>
    {
        return (await axios.delete(`simple-command/${this.dashboardApiUrl}/${publicId}`)).data;
    }

    public async getPermissions(sitemapId?: string): Promise<UserPermissions>
    {
        return (await axios.get(`${this.dashboardApiUrl}/actions`, {
            params: sitemapId ? { sitemapId } : undefined
        })).data;
    }

    public async getChartWidgetData(url: string, filter: any): Promise<ChartData>
    {
        return (
            await axios.get(url, {
                params: filter,
            })
        ).data;
    }

    public async getCountWidgetData(url: string, filter: any): Promise<any>
    {
        return (
            await axios.get(url, {
                params: filter,
            })
        ).data;
    }
}

export interface UserPermissions {
    canEditDefaultSettings: boolean;
}

export interface GridItemAttributes {
    id: string;
    "gs-id": string;
    "gs-x": number;
    "gs-y": number;
    "gs-w": number;
    "gs-h": number;
    "gs-no-resize"?: boolean;
}

export interface Widget {
    publicId: string;
    name: Record<string, string>;
    w: number;
    h: number;
    x?: number;
    y?: number;
    config?: APIWidget;
    canSee: boolean;
}

export interface WidgetSettings {
    config: Array<{ publicId: string; x: number; y: number, width: number, height: number, sourceConfig: any, canSee: boolean }>;
    publicId: string;
}


export interface WidgetModel {
    description: string;
    height: string;
    name: string;
    type: string;
    width: string;
    sourceConfig: Record<string, any> | string;
    sourceType: KeyValue<string, string>;
    headerIcon: string;
}

export interface APIWidget {
    actionButtons: string[],
    additionalData?: AdditionalData;
    dataEndpoint: string;
    description: Record<string, string>;
    height: KeyValue<string, string>;
    licence: KeyValue<string, string>;
    licenseUrl: string;
    actionUrl: string;
    moduleId: KeyValue<string, string>;
    name: Record<string, string>;
    publicId: string;
    sourceConfig: SourceConfigList | SourceConfigCount | SourceConfigEmployees | SourceConfigChart;
    sourceType: KeyValue<string, string>;
    staticWidgetType: KeyValue<string, string>;
    sitemapEntryDetails: SitemapEntryDetailsModel;
    width: KeyValue<string, string>;
    headerIcon: string;
    showFilters: boolean;
    showAddButton: boolean;
    showGroupedActionsButton: boolean;
    showPager: boolean;
    sitemapId: string;
}

interface SitemapEntryDetailsModel {
    entryType: SitemapEntryType;
    publicId: string;
}

interface AdditionalData {
    footerButtons: Array<IMetaModel>;
    headerButtons: Array<IMetaModel>;
    actionsUrl?: string;
    detailsUrl?: string;
}

export interface SourceConfigList {
    pageSize: number;
    listType: KeyValue<string, string>;
    columns: string;
    filter: string;
    sortField: FieldIndex;
    sortDirection: KeyValue<string, string>;
}

export interface SourceConfigCount {
    icon: string;
    iconColor: KeyValue<string, string>;
    numberColor: KeyValue<string, string>;
    filter: any;
    countType: KeyValue<string, string>;
}

export interface SourceConfigEmployees {}

export interface SourceConfigChart {
    chartType: KeyValue<string, string>;
    dateGroupType: KeyValue<string, string>;
    filter: any;
    icon: string;
    iconColor: KeyValue<string, string>;
    numberColor: KeyValue<string, string>;
    showValues?: boolean;
}


export interface FormFilter {
    search: string;
}

export interface ChartData {
    data: Array<{ amount: number; key: string; value: string }>;
    features: Array<KeyValue<string, string>>;
}

export enum WidgetTypeEnum {
    List = "List",
    Count = "Count",
    NewEmployees = "NewEmployees",
    ActiveSubstitutions = "ActiveSubstitutions",
    DoubleCounter = "DoubleCounter",
    Chart = "Chart",
    Sitemap = "Sitemap",
    Calendar = "Calendar",
    Timetable = "Timetable",
    GlobalTasks = "GlobalTasks",
    NewsList = "NewsList"
}

export enum WidgetActionsEnum {
    ShowAll = "show-all",
    Add = "add",
}

export enum SitemapEntryType {
    Node = "Node",
    Grid = "Grid",
    Calendar = "Calendar",
    Chart = "Chart",
    Timetable = "Timetable",
    Dashboard = "Dashboard",
}
